import React, { useState } from 'react';
import { Button } from '../UI/Button';
import { Card } from '../UI/Card';
import { CheckCircle2 } from 'lucide-react';
export const TrainingProposalForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    role: '',
    teamSize: '',
    trainingInterest: '',
    preferredFormat: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      // Using the existing formspree endpoint pattern from the codebase
      const response = await fetch('https://formspree.io/f/mjgavwrk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          _subject: `Training Proposal Request from ${formData.organization}`
        })
      });
      if (response.ok) {
        setIsSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          organization: '',
          role: '',
          teamSize: '',
          trainingInterest: '',
          preferredFormat: ''
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (err) {
      setError('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <section id="training-proposal-form" className="py-20 bg-white" data-id="element-1285">
        <div className="container mx-auto px-4" data-id="element-1286">
          <Card className="max-w-2xl mx-auto p-12 text-center border-2 border-green-100 bg-green-50/30" data-id="element-1287">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1288">
              <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-1289" />
            </div>
            <h3 className="text-3xl font-bold text-navy mb-4 font-heading" data-id="element-1290">
              Request Received!
            </h3>
            <p className="text-lg text-gray-700 mb-8" data-id="element-1291">
              Thank you for your interest in training with The Networking Salon.
              Our team is preparing a custom training proposal based on your
              selections and will send it to your email shortly.
            </p>
            <Button onClick={() => setIsSuccess(false)} data-id="element-1292">
              Submit Another Request
            </Button>
          </Card>
        </div>
      </section>;
  }
  return <section id="training-proposal-form" className="py-20 bg-white" data-id="element-1293">
      <div className="container mx-auto px-4" data-id="element-1294">
        <div className="max-w-3xl mx-auto scroll-animate" data-id="element-1295">
          <div className="text-center mb-12" data-id="element-1296">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4 font-heading" data-id="element-1297">
              Train Your Team With Us
            </h2>
            <p className="text-xl text-gray-600" data-id="element-1298">
              Fill out the form below to receive a custom training proposal PDF
              tailored to your organization's needs.
            </p>
          </div>

          <Card className="p-8 md:p-10 border-2 border-gray-200 shadow-lg" data-id="element-1299">
            {error && <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md mb-6" data-id="element-1300">
                {error}
              </div>}

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1301">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1302">
                <div data-id="element-1303">
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-2" data-id="element-1304">
                    Full Name *
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors" placeholder="Jane Doe" data-id="element-1305" />
                </div>
                <div data-id="element-1306">
                  <label htmlFor="organization" className="block text-gray-700 font-medium mb-2" data-id="element-1307">
                    Organization *
                  </label>
                  <input type="text" id="organization" name="organization" value={formData.organization} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors" placeholder="Acme Corp" data-id="element-1308" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1309">
                <div data-id="element-1310">
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2" data-id="element-1311">
                    Email Address *
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors" placeholder="jane@example.com" data-id="element-1312" />
                </div>
                <div data-id="element-1313">
                  <label htmlFor="phone" className="block text-gray-700 font-medium mb-2" data-id="element-1314">
                    Phone Number *
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors" placeholder="+234 800 000 0000" data-id="element-1315" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1316">
                <div data-id="element-1317">
                  <label htmlFor="role" className="block text-gray-700 font-medium mb-2" data-id="element-1318">
                    Your Role *
                  </label>
                  <input type="text" id="role" name="role" value={formData.role} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors" placeholder="HR Director" data-id="element-1319" />
                </div>
                <div data-id="element-1320">
                  <label htmlFor="teamSize" className="block text-gray-700 font-medium mb-2" data-id="element-1321">
                    Team Size *
                  </label>
                  <select id="teamSize" name="teamSize" value={formData.teamSize} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors bg-white" data-id="element-1322">
                    <option value="" data-id="element-1323">Select team size</option>
                    <option value="1-10" data-id="element-1324">1 - 10 people</option>
                    <option value="11-25" data-id="element-1325">11 - 25 people</option>
                    <option value="26-50" data-id="element-1326">26 - 50 people</option>
                    <option value="50+" data-id="element-1327">50+ people</option>
                  </select>
                </div>
              </div>

              <div data-id="element-1328">
                <label htmlFor="trainingInterest" className="block text-gray-700 font-medium mb-2" data-id="element-1329">
                  Primary Training Interest *
                </label>
                <select id="trainingInterest" name="trainingInterest" value={formData.trainingInterest} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors bg-white" data-id="element-1330">
                  <option value="" data-id="element-1331">Select an area of interest</option>
                  <option value="AI Training" data-id="element-1332">
                    AI Training & Productivity
                  </option>
                  <option value="Learning & Development" data-id="element-1333">
                    Learning & Development
                  </option>
                  <option value="Teacher Retooling" data-id="element-1334">
                    Teacher Retooling Programs
                  </option>
                  <option value="Communications" data-id="element-1335">
                    Communications & Soft Skills
                  </option>
                  <option value="Strategic Networking" data-id="element-1336">
                    Strategic Networking
                  </option>
                  <option value="Digital Growth" data-id="element-1337">
                    Social Media & Digital Growth
                  </option>
                  <option value="Community Building" data-id="element-1338">
                    Community Building & Management
                  </option>
                  <option value="Program Development" data-id="element-1339">
                    Program Development
                  </option>
                  <option value="Other" data-id="element-1340">Other (Custom Program)</option>
                </select>
              </div>

              <div data-id="element-1341">
                <label htmlFor="preferredFormat" className="block text-gray-700 font-medium mb-2" data-id="element-1342">
                  Preferred Training Format *
                </label>
                <select id="preferredFormat" name="preferredFormat" value={formData.preferredFormat} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors bg-white" data-id="element-1343">
                  <option value="" data-id="element-1344">Select format</option>
                  <option value="On-site" data-id="element-1345">On-site (In person)</option>
                  <option value="Virtual" data-id="element-1346">Virtual (Online)</option>
                  <option value="Hybrid" data-id="element-1347">Hybrid (Mix of both)</option>
                  <option value="Undecided" data-id="element-1348">Undecided</option>
                </select>
              </div>

              <div className="pt-4" data-id="element-1349">
                <Button type="submit" disabled={isSubmitting} className="w-full py-4 text-lg shadow-md" data-id="element-1350">
                  {isSubmitting ? 'Generating Proposal...' : 'Request Training Proposal'}
                </Button>
                <p className="text-center text-sm text-gray-500 mt-4" data-id="element-1351">
                  After submission, we'll generate and send a custom training
                  proposal PDF to your organization.
                </p>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </section>;
};